<script setup>
import {
  Bell,
  ChevronDown,
  LogOut,
  Menu,
  Search,
  Settings,
  User,
  X,
} from "lucide-vue-next";
import { ref } from "vue";
import { useClickOutside } from "../../hooks/use-click-outside";
import { userUserStore } from "../../store/useUserStore";
import { useRouter } from "vue-router";
import { getInitials } from "@/utils/helpers";

const notificationsOpen = ref(false);
const notificationsRef = ref(null);
const profileMenuOpen = ref(false);
const profileMenuRef = ref(null);
const userStore = userUserStore();
const router = useRouter();

useClickOutside([notificationsRef], () => (notificationsOpen.value = false));
useClickOutside([profileMenuRef], () => (profileMenuOpen.value = false));
const setNotificationOpen = () => {
  notificationsOpen.value = !notificationsOpen.value;
};

const toggleProfileMenu = () => {
  profileMenuOpen.value = !profileMenuOpen.value;
};
// Simular algumas notificações (você pode substituir por dados reais)
const notifications = [
  {
    id: 1,
    title: "Novo membro registrado",
    message: "João Silva se registrou como membro",
    time: "5 min atrás",
    unread: true,
  },
  {
    id: 2,
    title: "Pagamento processado",
    message: "Pagamento de Maria Santos foi aprovado",
    time: "10 min atrás",
    unread: true,
  },
  {
    id: 3,
    title: "Relatório disponível",
    message: "Relatório mensal de atividades está pronto",
    time: "1 hora atrás",
    unread: false,
  },
];

const handleLogout = () => {
  userStore.logout();
  router.push("/auth/login");
};
</script>
<template>
  <header class="bg-white border-b border-gray-200 sticky top-0 z-40">
    <div class="flex items-center justify-between px-4 lg:px-6 py-3">
      <div class="flex items-center gap-4">
        <button
          class="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
        >
          <Menu class="w-5 h-5" />
        </button>
      </div>

      <!-- search input -->
      <div class="hidden md:flex items-center flex-1 max-w-md mx-8">
        <div class="relative w-full">
          <Search
            class="text-gray-300 absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
          />

          <input
            type="text"
            placeholder="O que está a procurar...?"
            class="focus:outline-none w-full pl-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      <!-- right menu -->
      <div class="flex items-center gap-2">
        <button
          class="p-2 rounded-lg hover:bg-gray-100 transition-colors md:hidden"
        >
          <Search class="text-gray-600 w-5 h-5" />
        </button>

        <!-- Notifications -->
        <div class="relative" ref="notificationsRef">
          <button
            class="p-2 rounded-lg hover:bg-gray-100 text-green-700 transition-colors"
          >
            <Bell class="w-5 h-5" />
            <span
              class="absolute -top-1 -right-1 bg-red-600 text-white text-sm rounded-full w-5 h-5 flex items-center justify-center"
              >10</span
            >
          </button>
          <!-- notification dropdown -->

          <div
            v-if="notificationsOpen"
            class="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          >
            <div
              class="flex items-center justify-center p-4 border-b border-gray-200"
            >
              <h3 class="font-semibold text-gray-900">Notificações</h3>
              <button class="text-gray-400 hover:text-gray-600">
                <X class="w-5 h-5" />
              </button>
            </div>
            <div class="max-h-80 overflow-y-auto">
              <template v-for="n in notifications" :key="n.id">
                <div
                  class="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  :class="n.unread ? 'bg-blue-50' : ''"
                >
                  <div class="flex items-start gap-3">
                    <div
                      class="w-2 h-2 rounded-full mt-2"
                      :class="n.unread ? 'bg-green-500' : 'bg-gray-300'"
                    />
                    <div class="flex-1">
                      <h4 class="text-sm font-medium text-gray-900">
                        {{ n.title }}
                      </h4>
                      <p class="text-sm text-gray-600 mt-1">
                        {{ n.message }}
                      </p>
                      <p class="text-xs text-gray-400">
                        {{ n.time }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
              <div className="p-3 border-t border-gray-200">
                <button
                  className="w-full text-center text-sm text-tservebg hover:text-green-700 font-medium"
                >
                  Ver todas as notificações
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="relative" ref="profileMenuRef">
          <button
            @click="toggleProfileMenu"
            class="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div
              class="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center"
            >
              {{ getInitials(userStore.user?.person?.fullName) }}
            </div>
            <div class="hidden sm:block text-left">
              <p class="text-sm font-medium text-gray-900">
                {{ getInitials(userStore.user?.person?.fullName) }}
              </p>
            </div>
            <ChevronDown class="w-5 h-5 text-gray-400 hidden sm:block" />
          </button>
          <div
            v-if="profileMenuOpen"
            class="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          >
            <div class="p-4 border-b border-gray-200">
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"
                >
                  {{ getInitials(userStore.user?.person?.fullName) }}
                </div>
                <div>
                  <p>{{ userStore.user?.person?.fullName }}</p>
                  <p class="text-xs text-gray-500">
                    {{ userStore.user?.email }}
                  </p>
                </div>
              </div>
            </div>

            <div class="p-2">
              <button
                class="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <User class="w-5 h-5" />
                Meu Perfil
              </button>
              <button
                class="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Settings class="w-5 h-5" />
                Configurações
              </button>
            </div>
            <div class="p-2 border-t border-gray-200">
              <button
                @click="handleLogout"
                type="button"
                class="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut class="w-5 h-5" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>